import type { LiveOverlay } from '@/types';
import { logoMaskStyle } from './masks';

// The pieces the three OBS browser sources are made of. They composite over the video
// feed, so the page keeps its background transparent and the elements just
// appear/disappear — OBS owns the transitions.
//
// Colours come from the theme-ph26 tokens: primary = brand yellow, background =
// near-black.

// Where the page turns itself transparent. Rendered by all three overlays.
export const OVERLAY_TRANSPARENCY = `
  html, body { background: transparent !important; }
  .theme-ph26 { background-color: transparent !important; }
`;

const CARD_CLASS =
  'rounded-[6px] border border-primary bg-background/85 px-5 py-3 text-primary whitespace-nowrap';

export function Wordmark({ overlay }: { overlay: LiveOverlay }) {
  return (
    <div className={overlay.wordmarkClass}>
      <div className="whitespace-nowrap font-logo text-4xl text-primary lowercase leading-none tracking-tight">
        <span className="font-light">platanus hack</span>{' '}
        <span className="font-medium">[26]</span>
      </div>
      <div className={overlay.ftRowClass}>
        <span className="font-sans text-primary text-sm uppercase leading-none tracking-[0.15em]">
          ft.
        </span>
        <div
          className={overlay.ftLogo.className}
          style={{
            ...logoMaskStyle(overlay.ftLogo.src),
            width: overlay.ftLogo.width,
            height: overlay.ftLogo.height,
          }}
        />
        <span className="font-sans text-primary/50 text-sm leading-none">—</span>
        <span className="font-sans text-primary text-sm uppercase leading-none tracking-[0.15em]">
          {overlay.city}
        </span>
      </div>
    </div>
  );
}

export function TrackBadge({
  overlay,
  name,
}: {
  overlay: LiveOverlay;
  name: string;
}) {
  return (
    <div className={overlay.trackBadgeClass}>
      <div className={CARD_CLASS}>
        <span className="font-sans text-xl uppercase leading-none tracking-wide">
          {name}
        </span>
      </div>
    </div>
  );
}

export function SponsorStrip({ overlay }: { overlay: LiveOverlay }) {
  const set = (
    <div className="flex shrink-0 items-center">
      {overlay.sponsors.map((s) => (
        <div key={s.name} className="flex items-center px-9">
          <div
            role="img"
            aria-label={s.name}
            className="bg-background"
            style={{ ...logoMaskStyle(s.src), width: s.width, height: 40 }}
          />
        </div>
      ))}
    </div>
  );

  return (
    <div className="flex h-14 w-full items-center overflow-hidden bg-primary">
      <div
        className="flex w-max items-center"
        style={{ animation: 'scroll-slow 45s linear infinite' }}
      >
        {set}
        {set}
      </div>
    </div>
  );
}

// Diagonal arrow pointing top-right — separator between hacker names.
function Arrow({ size = 30 }: { size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 104 106"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className="shrink-0 text-primary"
      aria-hidden="true"
    >
      <path
        d="M17.3514 106L0 87.8523L65.9813 21.4832L34.987 21.8916V0H104V69.5549H81.4107L81.8168 38.9366L17.3514 106Z"
        fill="currentColor"
      />
    </svg>
  );
}

export function NamesTicker({
  members,
}: {
  members: Array<{ fullName: string }>;
}) {
  const set = (
    <div className="flex shrink-0 items-center">
      {members.map((m, i) => (
        <div
          key={`${m.fullName}-${i}`}
          className="flex items-center gap-7 pl-7"
        >
          <span className="whitespace-nowrap font-bold font-sans text-[40px] text-primary uppercase leading-none tracking-wide underline decoration-4 underline-offset-[8px]">
            {m.fullName}
          </span>
          <Arrow />
        </div>
      ))}
    </div>
  );

  return (
    <div className="flex h-14 w-full items-center overflow-hidden bg-background">
      <div
        className="flex w-max items-center"
        style={{ animation: 'scroll-slow 30s linear infinite' }}
      >
        {set}
        {set}
      </div>
    </div>
  );
}
